import React, { useState, useEffect } from 'react';
import { 
  Settings, Ship, DollarSign, Users, Plus, Edit2, Trash2, Save, X, 
  AlertCircle, Check, Factory, Image, Link, ArrowLeft,
  ThumbsUp, Clock, Eye, CheckCircle, XCircle, Search, MessageSquare,
  AlertTriangle, RefreshCw, MapPin, Loader2
} from 'lucide-react';
import { issuesApi } from '../services/issuesApi';
import { rebatesApi } from '../services/rebatesApi';
import { dealersApi } from '../services/dealersApi';

const AdminDashboard = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('issues');
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Factory Boats State
  const [factoryBoats, setFactoryBoats] = useState(() => {
    const stored = localStorage.getItem('admin_factory_boats');
    return stored ? JSON.parse(stored) : [];
  });
  const [editingBoat, setEditingBoat] = useState(null);
  const [showBoatForm, setShowBoatForm] = useState(false);
  
  // Rebates State
  const [rebates, setRebates] = useState([]);
  const [rebatesLoading, setRebatesLoading] = useState(false);
  const [rebatesError, setRebatesError] = useState(null);
  const [editingRebate, setEditingRebate] = useState(null);
  const [showRebateForm, setShowRebateForm] = useState(false);
  
  // Dealers State
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [dealersError, setDealersError] = useState(null);
  const [editingDealer, setEditingDealer] = useState(null);
  const [showDealerForm, setShowDealerForm] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState(null);

  // Issues State
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issueStats, setIssueStats] = useState(null);
  const [editingIssue, setEditingIssue] = useState(null);
  const [issueSearchTerm, setIssueSearchTerm] = useState('');
  const [issueStatusFilter, setIssueStatusFilter] = useState('all');

  // New Boat Form State
  const emptyBoat = {
    id: '',
    model: '',
    year: new Date().getFullYear(),
    series: 'Ri Series',
    msrp: '',
    description: '',
    features: [],
    status: 'available',
    imageUrl: '',
    imageUrls: [] // Support for multiple images
  };
  const [boatForm, setBoatForm] = useState(emptyBoat);
  const [newImageUrl, setNewImageUrl] = useState('');

  // New Rebate Form State
  const emptyRebate = {
    id: '',
    title: '',
    brand: 'Centurion',
    amount: '',
    amountText: '',
    startDate: '',
    endDate: '',
    status: 'active',
    models: [],
    description: '',
    terms: [],
    featured: false
  };
  const [rebateForm, setRebateForm] = useState(emptyRebate);

  // New Dealer Form State
  const emptyDealer = {
    id: '',
    dealerName: '',
    contactName: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    latitude: '',
    longitude: '',
    dealerType: 'Authorized',
    brands: 'Centurion, Supreme',
    services: 'Sales, Service, Parts',
    hours: '',
    logoURL: '',
    status: 'Active',
    notes: ''
  };
  const [dealerForm, setDealerForm] = useState(emptyDealer);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('admin_factory_boats', JSON.stringify(factoryBoats));
  }, [factoryBoats]);

  // Load rebates from API
  const loadRebates = async () => {
    try {
      setRebatesLoading(true);
      setRebatesError(null);
      const data = await rebatesApi.getAll();
      setRebates(data.rebates || []);
    } catch (err) {
      console.error('Error loading rebates:', err);
      setRebatesError('Failed to load rebates. Please try again.');
    } finally {
      setRebatesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'rebates') {
      loadRebates();
    }
    if (activeTab === 'dealers') {
      loadDealers();
    }
  }, [activeTab]);

  // Load dealers from API
  const loadDealers = async () => {
    try {
      setDealersLoading(true);
      setDealersError(null);
      const token = localStorage.getItem('adminToken');
      let data;
      if (token) {
        data = await dealersApi.getAdminDealers(token);
      } else {
        data = await dealersApi.getAll();
      }
      setDealers(data.dealers || []);
    } catch (err) {
      console.error('Error loading dealers:', err);
      setDealersError('Failed to load dealers. Please try again.');
    } finally {
      setDealersLoading(false);
    }
  };

  const showSaveNotification = () => {
    setSaveStatus('success');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  // Factory Boats Functions
  const handleSaveBoat = () => {
    if (editingBoat) {
      setFactoryBoats(prev => prev.map(b => b.id === editingBoat.id ? { ...boatForm, id: editingBoat.id } : b));
    } else {
      setFactoryBoats(prev => [...prev, { ...boatForm, id: Date.now().toString() }]);
    }
    setBoatForm(emptyBoat);
    setEditingBoat(null);
    setShowBoatForm(false);
    showSaveNotification();
  };

  const handleEditBoat = (boat) => {
    setBoatForm(boat);
    setEditingBoat(boat);
    setShowBoatForm(true);
  };

  const handleDeleteBoat = (id) => {
    if (window.confirm('Are you sure you want to delete this boat?')) {
      setFactoryBoats(prev => prev.filter(b => b.id !== id));
      showSaveNotification();
    }
  };

  // Rebates Functions
  const handleSaveRebate = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please log out and log back in.');
        return;
      }

      if (editingRebate) {
        await rebatesApi.update(editingRebate.id, rebateForm, token);
      } else {
        await rebatesApi.create(rebateForm, token);
      }
      
      await loadRebates();
      setRebateForm(emptyRebate);
      setEditingRebate(null);
      setShowRebateForm(false);
      showSaveNotification();
    } catch (err) {
      console.error('Error saving rebate:', err);
      alert('Failed to save rebate: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditRebate = (rebate) => {
    // Normalize field names for the form
    setRebateForm({
      ...rebate,
      amountText: rebate.amountText || rebate.amounttext || '',
      startDate: rebate.startDate || rebate.startdate || '',
      endDate: rebate.endDate || rebate.enddate || ''
    });
    setEditingRebate(rebate);
    setShowRebateForm(true);
  };

  const handleDeleteRebate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rebate?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please log out and log back in.');
        return;
      }

      await rebatesApi.delete(id, token);
      await loadRebates();
      showSaveNotification();
    } catch (err) {
      console.error('Error deleting rebate:', err);
      alert('Failed to delete rebate: ' + (err.response?.data?.error || err.message));
    }
  };

  // Dealers Functions
  const handleGeocodeAddress = async () => {
    if (!dealerForm.address || !dealerForm.city || !dealerForm.state || !dealerForm.zipCode) {
      setGeocodeStatus({ type: 'error', message: 'Please fill in address, city, state, and zip code first' });
      return;
    }

    setGeocoding(true);
    setGeocodeStatus(null);

    try {
      const result = await dealersApi.geocodeAddress(
        dealerForm.address,
        dealerForm.city,
        dealerForm.state,
        dealerForm.zipCode
      );

      if (result) {
        setDealerForm({
          ...dealerForm,
          latitude: result.latitude,
          longitude: result.longitude
        });
        setGeocodeStatus({ type: 'success', message: `Found: ${result.displayName}` });
      } else {
        setGeocodeStatus({ type: 'error', message: 'Could not find coordinates for this address' });
      }
    } catch (error) {
      setGeocodeStatus({ type: 'error', message: 'Geocoding failed. Please enter coordinates manually.' });
    } finally {
      setGeocoding(false);
    }
  };

  const handleSaveDealer = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please log out and log back in.');
        return;
      }

      if (!dealerForm.dealerName || !dealerForm.email || !dealerForm.phone) {
        alert('Please fill in dealer name, email, and phone.');
        return;
      }

      if (editingDealer) {
        await dealersApi.update(editingDealer.id, dealerForm, token);
      } else {
        await dealersApi.create(dealerForm, token);
      }

      await loadDealers();
      setDealerForm(emptyDealer);
      setEditingDealer(null);
      setShowDealerForm(false);
      setGeocodeStatus(null);
      showSaveNotification();
    } catch (err) {
      console.error('Error saving dealer:', err);
      alert('Failed to save dealer: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditDealer = (dealer) => {
    setDealerForm({
      ...emptyDealer,
      ...dealer
    });
    setEditingDealer(dealer);
    setShowDealerForm(true);
    setGeocodeStatus(null);
  };

  const handleDeleteDealer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this dealer?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please log out and log back in.');
        return;
      }

      await dealersApi.delete(id, token);
      await loadDealers();
      showSaveNotification();
    } catch (err) {
      console.error('Error deleting dealer:', err);
      alert('Failed to delete dealer: ' + (err.response?.data?.error || err.message));
    }
  };

  // Check for existing admin token on mount
  useEffect(() => {
    const existingToken = localStorage.getItem('adminToken');
    if (existingToken) {
      console.log('✅ Admin token found');
    } else {
      console.log('⚠️ No admin token - some features may require authentication');
    }
  }, []);

  // Issues Functions
  const loadIssues = async () => {
    try {
      setIssuesLoading(true);
      const token = localStorage.getItem('adminToken');
      if (token) {
        const [issuesData, statsData] = await Promise.all([
          issuesApi.getAdminIssues(token),
          issuesApi.getStats(token)
        ]);
        setIssues(issuesData.issues || []);
        setIssueStats(statsData);
      } else {
        // Fallback to public API if no admin token
        const data = await issuesApi.getIssues({});
        setIssues(data.issues || []);
      }
    } catch (err) {
      console.error('Error loading issues:', err);
      // Try public API as fallback
      try {
        const data = await issuesApi.getIssues({});
        setIssues(data.issues || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setIssuesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'issues') {
      loadIssues();
    }
  }, [activeTab]);

  const handleIssueStatusChange = async (issueId, newStatus, resolutionNotes = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please log out and log back in.');
        return;
      }
      
      await issuesApi.updateStatus(issueId, newStatus, resolutionNotes, token);
      await loadIssues();
      showSaveNotification();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateIssue = async (issueId, updates) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please log out and log back in.');
        return;
      }
      
      await issuesApi.updateIssue(issueId, updates, token);
      await loadIssues();
      setEditingIssue(null);
      showSaveNotification();
    } catch (err) {
      console.error('Error updating issue:', err);
      alert('Failed to update issue: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Are you sure you want to delete this issue? This cannot be undone.')) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please log out and log back in.');
        return;
      }
      
      await issuesApi.deleteIssue(issueId, token);
      await loadIssues();
      showSaveNotification();
    } catch (err) {
      console.error('Error deleting issue:', err);
      alert('Failed to delete issue: ' + (err.response?.data?.error || err.message));
    }
  };

  const filteredIssues = issues.filter(issue => {
    const matchesSearch = !issueSearchTerm || 
      issue.title?.toLowerCase().includes(issueSearchTerm.toLowerCase()) ||
      issue.id?.toLowerCase().includes(issueSearchTerm.toLowerCase()) ||
      issue.category?.toLowerCase().includes(issueSearchTerm.toLowerCase());
    const matchesStatus = issueStatusFilter === 'all' || issue.status === issueStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      'Open': '#8B0000',
      'In Progress': '#FF8C00',
      'Under Review': '#4169E1',
      'Resolved': '#228B22',
      'Closed': '#808080'
    };
    return colors[status] || '#666';
  };

  const getStatusBgColor = (status) => {
    const colors = {
      'Open': '#FFE5E5',
      'In Progress': '#FFF4E5',
      'Under Review': '#E5F0FF',
      'Resolved': '#E5FFE5',
      'Closed': '#F0F0F0'
    };
    return colors[status] || '#F5F5F5';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Open': AlertCircle,
      'In Progress': Clock,
      'Under Review': Eye,
      'Resolved': CheckCircle,
      'Closed': XCircle
    };
    return icons[status] || AlertCircle;
  };

  const ISSUE_STATUSES = ['Open', 'In Progress', 'Under Review', 'Resolved', 'Closed'];
  const ISSUE_PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

  const tabs = [
    { id: 'issues', label: 'Issues', icon: AlertTriangle, count: issues.length },
    { id: 'factory', label: 'Factory Boats', icon: Factory, count: factoryBoats.length },
    { id: 'rebates', label: 'Rebates', icon: DollarSign, count: rebates.length },
    { id: 'dealers', label: 'Dealers', icon: Users, count: dealers.length },
  ];

  return (
    <div className="admin-dashboard">
      {/* Admin Navigation Bar */}
      <nav className="admin-nav-bar">
        <div className="admin-nav-container">
          <button className="back-to-site-btn" onClick={() => onNavigate('inventory')}>
            <ArrowLeft size={18} />
            Exit Admin
          </button>
          
          <div className="admin-nav-tabs">
            {tabs.map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`admin-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <IconComponent size={18} />
                  <span>{tab.label}</span>
                  <span className="nav-count">{tab.count}</span>
                </button>
              );
            })}
          </div>
          
          <div className="admin-badge">
            <Settings size={16} />
            Admin Mode
          </div>
        </div>
      </nav>
      
      <div className="page-header admin-header">
        <Settings size={40} />
        <h1>Admin Dashboard</h1>
        <p>Manage Issues, Factory Boats, Rebates, and Dealer Network</p>
      </div>

      {saveStatus === 'success' && (
        <div className="save-notification success">
          <Check size={20} />
          <span>Changes saved successfully!</span>
        </div>
      )}

      {/* Issues Tab */}
      {activeTab === 'issues' && (
        <div className="admin-section">
          {/* Issue Stats */}
          {issueStats && (
            <div className="issue-stats-grid" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
              gap: '1rem', 
              marginBottom: '2rem' 
            }}>
              <div className="stat-card" style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1a2332' }}>{issueStats.total}</div>
                <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase' }}>Total Issues</div>
              </div>
              <div className="stat-card" style={{ 
                background: '#FFE5E5', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8B0000' }}>{issueStats.totalByStatus?.Open || 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#8B0000', textTransform: 'uppercase' }}>Open</div>
              </div>
              <div className="stat-card" style={{ 
                background: '#FFF4E5', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FF8C00' }}>{issueStats.totalByStatus?.['In Progress'] || 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#FF8C00', textTransform: 'uppercase' }}>In Progress</div>
              </div>
              <div className="stat-card" style={{ 
                background: '#E5F0FF', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4169E1' }}>{issueStats.totalByStatus?.['Under Review'] || 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#4169E1', textTransform: 'uppercase' }}>Under Review</div>
              </div>
              <div className="stat-card" style={{ 
                background: '#E5FFE5', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#228B22' }}>{issueStats.totalByStatus?.Resolved || 0}</div>
                <div style={{ fontSize: '0.8rem', color: '#228B22', textTransform: 'uppercase' }}>Resolved</div>
              </div>
            </div>
          )}

          <div className="section-header">
            <h2><AlertTriangle size={24} /> Issue Management</h2>
            <button className="add-button" onClick={() => onNavigate('issues-submit')}>
              <Plus size={18} />
              Submit Issue
            </button>
          </div>

          {/* Search and Filter */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
              <input
                type="text"
                placeholder="Search issues..."
                value={issueSearchTerm}
                onChange={(e) => setIssueSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              />
            </div>
            <select
              value={issueStatusFilter}
              onChange={(e) => setIssueStatusFilter(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                minWidth: '150px'
              }}
            >
              <option value="all">All Statuses</option>
              {ISSUE_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <button 
              onClick={loadIssues}
              style={{
                padding: '0.75rem 1rem',
                background: '#D4AF37',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Refresh
            </button>
          </div>

          {/* Issues List */}
          <div className="items-list">
            {issuesLoading ? (
              <div className="empty-state">
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '3px solid #f0f0f0', 
                  borderTopColor: '#D4AF37',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem'
                }} />
                <p>Loading issues...</p>
              </div>
            ) : filteredIssues.length === 0 ? (
              <div className="empty-state">
                <AlertTriangle size={48} />
                <p>{issues.length === 0 ? 'No issues submitted yet.' : 'No issues match your search.'}</p>
              </div>
            ) : (
              filteredIssues.map(issue => {
                const StatusIcon = getStatusIcon(issue.status);
                return (
                  <div key={issue.id} className="item-card" style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '1rem',
                    padding: '1.25rem'
                  }}>
                    {/* Issue Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <span style={{ 
                            fontFamily: 'monospace', 
                            fontSize: '0.8rem', 
                            color: '#999',
                            background: '#f5f5f5',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px'
                          }}>{issue.id}</span>
                          <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            padding: '0.3rem 0.6rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            background: getStatusBgColor(issue.status),
                            color: getStatusColor(issue.status)
                          }}>
                            <StatusIcon size={12} />
                            {issue.status}
                          </span>
                          {issue.priority && issue.priority !== 'Medium' && (
                            <span style={{
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              background: issue.priority === 'Critical' ? '#8B0000' : issue.priority === 'High' ? '#FF8C00' : '#E0E0E0',
                              color: issue.priority === 'Critical' || issue.priority === 'High' ? 'white' : '#666'
                            }}>
                              {issue.priority}
                            </span>
                          )}
                        </div>
                        <h4 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#1a2332' }}>{issue.title}</h4>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#666', flexWrap: 'wrap' }}>
                          <span style={{ 
                            background: '#f5f5f5', 
                            padding: '0.2rem 0.5rem', 
                            borderRadius: '4px' 
                          }}>{issue.category}</span>
                          {issue.boatModel && (
                            <span style={{ 
                              background: '#FFF9E5', 
                              color: '#B8941F',
                              padding: '0.2rem 0.5rem', 
                              borderRadius: '4px' 
                            }}>{issue.year} {issue.boatModel}</span>
                          )}
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <ThumbsUp size={12} /> {issue.upvotes || 0}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <MessageSquare size={12} /> {issue.commentCount || 0}
                          </span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <button className="edit-btn" onClick={() => setEditingIssue(issue)} title="Edit">
                          <Edit2 size={16} />
                        </button>
                        <button className="delete-btn" onClick={() => handleDeleteIssue(issue.id)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Issue Description Preview */}
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.9rem', 
                      color: '#666',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {issue.description}
                    </p>
                    
                    {/* Status Change Buttons */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.5rem', 
                      flexWrap: 'wrap',
                      borderTop: '1px solid #f0f0f0',
                      paddingTop: '1rem'
                    }}>
                      <span style={{ fontSize: '0.8rem', color: '#999', marginRight: '0.5rem', alignSelf: 'center' }}>
                        Change status:
                      </span>
                      {ISSUE_STATUSES.map(status => (
                        <button
                          key={status}
                          onClick={() => handleIssueStatusChange(issue.id, status)}
                          disabled={issue.status === status}
                          style={{
                            padding: '0.4rem 0.75rem',
                            borderRadius: '6px',
                            border: issue.status === status ? '2px solid ' + getStatusColor(status) : '1px solid #ddd',
                            background: issue.status === status ? getStatusBgColor(status) : 'white',
                            color: issue.status === status ? getStatusColor(status) : '#666',
                            cursor: issue.status === status ? 'default' : 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: issue.status === status ? '600' : '400',
                            opacity: issue.status === status ? 1 : 0.8,
                            transition: 'all 0.2s'
                          }}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                    
                    {/* Contact Info */}
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#999',
                      display: 'flex',
                      gap: '1.5rem',
                      flexWrap: 'wrap'
                    }}>
                      <span><strong>Contact:</strong> {issue.contactName}</span>
                      <span><strong>Email:</strong> {issue.contactEmail}</span>
                      {issue.phone && <span><strong>Phone:</strong> {issue.phone}</span>}
                      <span><strong>Submitted:</strong> {new Date(issue.timestamp).toLocaleDateString()}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Issue Edit Modal */}
      {editingIssue && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }} onClick={() => setEditingIssue(null)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ 
              padding: '1.5rem', 
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Edit Issue - {editingIssue.id}</h2>
              <button onClick={() => setEditingIssue(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editingIssue.status}
                    onChange={(e) => setEditingIssue({ ...editingIssue, status: e.target.value })}
                  >
                    {ISSUE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={editingIssue.priority || 'Medium'}
                    onChange={(e) => setEditingIssue({ ...editingIssue, priority: e.target.value })}
                  >
                    {ISSUE_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Assigned To</label>
                  <input
                    type="text"
                    value={editingIssue.assignedTo || ''}
                    onChange={(e) => setEditingIssue({ ...editingIssue, assignedTo: e.target.value })}
                    placeholder="Team member name"
                  />
                </div>
                {(editingIssue.status === 'Resolved' || editingIssue.status === 'Closed') && (
                  <div className="form-group full-width">
                    <label>Resolution Notes</label>
                    <textarea
                      value={editingIssue.resolutionNotes || ''}
                      onChange={(e) => setEditingIssue({ ...editingIssue, resolutionNotes: e.target.value })}
                      placeholder="Describe how the issue was resolved..."
                      rows={3}
                    />
                  </div>
                )}
                <div className="form-group full-width">
                  <label>Admin Notes (Internal - not visible to public)</label>
                  <textarea
                    value={editingIssue.adminNotes || ''}
                    onChange={(e) => setEditingIssue({ ...editingIssue, adminNotes: e.target.value })}
                    placeholder="Internal notes for your team..."
                    rows={3}
                  />
                </div>
              </div>
              
              {/* Issue Details (Read Only) */}
              <div style={{ 
                marginTop: '1.5rem', 
                padding: '1rem', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                fontSize: '0.9rem'
              }}>
                <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#999', textTransform: 'uppercase' }}>Issue Details</h4>
                <p style={{ margin: '0 0 0.5rem' }}><strong>Title:</strong> {editingIssue.title}</p>
                <p style={{ margin: '0 0 0.5rem' }}><strong>Category:</strong> {editingIssue.category}</p>
                {editingIssue.boatModel && <p style={{ margin: '0 0 0.5rem' }}><strong>Model:</strong> {editingIssue.year} {editingIssue.boatModel}</p>}
                <p style={{ margin: '0 0 0.5rem' }}><strong>Contact:</strong> {editingIssue.contactName} ({editingIssue.contactEmail})</p>
                <p style={{ margin: '0', whiteSpace: 'pre-wrap' }}><strong>Description:</strong><br/>{editingIssue.description}</p>
              </div>
            </div>
            <div style={{ 
              padding: '1rem 1.5rem', 
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem'
            }}>
              <button className="cancel-btn" onClick={() => setEditingIssue(null)}>
                <X size={16} /> Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={() => handleUpdateIssue(editingIssue.id, {
                  status: editingIssue.status,
                  priority: editingIssue.priority,
                  assignedTo: editingIssue.assignedTo,
                  resolutionNotes: editingIssue.resolutionNotes,
                  adminNotes: editingIssue.adminNotes
                })}
              >
                <Save size={16} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Factory Boats Tab */}
      {activeTab === 'factory' && (
        <div className="admin-section">
          <div className="section-header">
            <h2><Ship size={24} /> Factory Direct Boats</h2>
            <button className="add-button" onClick={() => { setShowBoatForm(true); setEditingBoat(null); setBoatForm(emptyBoat); }}>
              <Plus size={18} />
              Add Boat
            </button>
          </div>

          {showBoatForm && (
            <div className="admin-form">
              <h3>{editingBoat ? 'Edit Boat' : 'Add New Boat'}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Model</label>
                  <input
                    type="text"
                    value={boatForm.model}
                    onChange={(e) => setBoatForm({ ...boatForm, model: e.target.value })}
                    placeholder="e.g., Ri230"
                  />
                </div>
                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="number"
                    value={boatForm.year}
                    onChange={(e) => setBoatForm({ ...boatForm, year: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>Series</label>
                  <select
                    value={boatForm.series}
                    onChange={(e) => setBoatForm({ ...boatForm, series: e.target.value })}
                  >
                    <option value="Ri Series">Ri Series</option>
                    <option value="Fi Series">Fi Series</option>
                    <option value="Vi Series">Vi Series</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>MSRP</label>
                  <input
                    type="number"
                    value={boatForm.msrp}
                    onChange={(e) => setBoatForm({ ...boatForm, msrp: e.target.value })}
                    placeholder="e.g., 150000"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={boatForm.status}
                    onChange={(e) => setBoatForm({ ...boatForm, status: e.target.value })}
                  >
                    <option value="available">Available</option>
                    <option value="demo">Demo Unit</option>
                    <option value="coming_soon">Coming Soon</option>
                  </select>
                </div>
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={boatForm.description}
                  onChange={(e) => setBoatForm({ ...boatForm, description: e.target.value })}
                  placeholder="Boat description..."
                  rows={3}
                />
              </div>
              
              {/* Image Management Section */}
              <div className="form-group full-width image-section">
                <label><Image size={16} /> Boat Images</label>
                <div className="image-url-input">
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Paste image URL (e.g., https://example.com/boat.jpg)"
                  />
                  <button 
                    type="button"
                    className="add-image-btn"
                    onClick={() => {
                      if (newImageUrl.trim()) {
                        setBoatForm({ 
                          ...boatForm, 
                          imageUrls: [...(boatForm.imageUrls || []), newImageUrl.trim()],
                          imageUrl: boatForm.imageUrl || newImageUrl.trim() // Set first as primary
                        });
                        setNewImageUrl('');
                      }
                    }}
                  >
                    <Plus size={16} /> Add Image
                  </button>
                </div>
                
                {/* Image Preview Grid */}
                {boatForm.imageUrls && boatForm.imageUrls.length > 0 && (
                  <div className="image-preview-grid">
                    {boatForm.imageUrls.map((url, index) => (
                      <div key={index} className={`image-preview-item ${url === boatForm.imageUrl ? 'primary' : ''}`}>
                        <img src={url} alt={`Boat preview ${index + 1}`} />
                        <div className="image-preview-actions">
                          <button 
                            type="button"
                            className={`set-primary-btn ${url === boatForm.imageUrl ? 'active' : ''}`}
                            onClick={() => setBoatForm({ ...boatForm, imageUrl: url })}
                            title="Set as primary image"
                          >
                            ★
                          </button>
                          <button 
                            type="button"
                            className="remove-image-btn"
                            onClick={() => {
                              const newUrls = boatForm.imageUrls.filter((_, i) => i !== index);
                              setBoatForm({ 
                                ...boatForm, 
                                imageUrls: newUrls,
                                imageUrl: url === boatForm.imageUrl ? (newUrls[0] || '') : boatForm.imageUrl
                              });
                            }}
                            title="Remove image"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        {url === boatForm.imageUrl && <span className="primary-badge">Primary</span>}
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="image-help-text">
                  <Link size={12} /> Add image URLs from the web. The first image (or one marked with ★) will be the primary display image.
                </p>
              </div>
              
              <div className="form-actions">
                <button className="save-btn" onClick={handleSaveBoat}>
                  <Save size={16} /> Save Boat
                </button>
                <button className="cancel-btn" onClick={() => { setShowBoatForm(false); setEditingBoat(null); setNewImageUrl(''); }}>
                  <X size={16} /> Cancel
                </button>
              </div>
            </div>
          )}

          <div className="items-list">
            {factoryBoats.length === 0 ? (
              <div className="empty-state">
                <Ship size={48} />
                <p>No factory boats added yet. Click "Add Boat" to get started.</p>
              </div>
            ) : (
              factoryBoats.map(boat => (
                <div key={boat.id} className="item-card with-image">
                  {boat.imageUrl && (
                    <div className="item-image">
                      <img src={boat.imageUrl} alt={`${boat.year} ${boat.model}`} />
                    </div>
                  )}
                  <div className="item-info">
                    <h4>{boat.year} {boat.model}</h4>
                    <p>{boat.series} • ${boat.msrp?.toLocaleString() || 'TBD'} • {boat.status}</p>
                    {boat.imageUrls && boat.imageUrls.length > 0 && (
                      <span className="image-count"><Image size={12} /> {boat.imageUrls.length} image{boat.imageUrls.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="item-actions">
                    <button className="edit-btn" onClick={() => handleEditBoat(boat)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="delete-btn" onClick={() => handleDeleteBoat(boat.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Rebates Tab */}
      {activeTab === 'rebates' && (
        <div className="admin-section">
          <div className="section-header">
            <h2><DollarSign size={24} /> Rebates & Incentives</h2>
            <button className="add-button" onClick={() => { setShowRebateForm(true); setEditingRebate(null); setRebateForm(emptyRebate); }}>
              <Plus size={18} />
              Add Rebate
            </button>
          </div>

          {showRebateForm && (
            <div className="admin-form">
              <h3>{editingRebate ? 'Edit Rebate' : 'Add New Rebate'}</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Title</label>
                  <input
                    type="text"
                    value={rebateForm.title}
                    onChange={(e) => setRebateForm({ ...rebateForm, title: e.target.value })}
                    placeholder="e.g., End of Year Clearance"
                  />
                </div>
                <div className="form-group">
                  <label>Brand</label>
                  <select
                    value={rebateForm.brand}
                    onChange={(e) => setRebateForm({ ...rebateForm, brand: e.target.value })}
                  >
                    <option value="Centurion">Centurion</option>
                    <option value="Supreme">Supreme</option>
                    <option value="All">All Brands</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    value={rebateForm.amount}
                    onChange={(e) => setRebateForm({ ...rebateForm, amount: e.target.value })}
                    placeholder="e.g., 5000"
                  />
                </div>
                <div className="form-group">
                  <label>Amount Text</label>
                  <input
                    type="text"
                    value={rebateForm.amountText}
                    onChange={(e) => setRebateForm({ ...rebateForm, amountText: e.target.value })}
                    placeholder="e.g., $5,000 OFF"
                  />
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={rebateForm.startDate}
                    onChange={(e) => setRebateForm({ ...rebateForm, startDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={rebateForm.endDate}
                    onChange={(e) => setRebateForm({ ...rebateForm, endDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={rebateForm.status}
                    onChange={(e) => setRebateForm({ ...rebateForm, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Featured</label>
                  <select
                    value={rebateForm.featured ? 'yes' : 'no'}
                    onChange={(e) => setRebateForm({ ...rebateForm, featured: e.target.value === 'yes' })}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={rebateForm.description}
                  onChange={(e) => setRebateForm({ ...rebateForm, description: e.target.value })}
                  placeholder="Rebate description..."
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button className="save-btn" onClick={handleSaveRebate}>
                  <Save size={16} /> Save Rebate
                </button>
                <button className="cancel-btn" onClick={() => { setShowRebateForm(false); setEditingRebate(null); }}>
                  <X size={16} /> Cancel
                </button>
              </div>
            </div>
          )}

          <div className="items-list">
            {rebatesLoading ? (
              <div className="empty-state">
                <RefreshCw size={48} className="spin" />
                <p>Loading rebates...</p>
              </div>
            ) : rebatesError ? (
              <div className="empty-state" style={{ color: '#8B0000' }}>
                <AlertCircle size={48} />
                <p>{rebatesError}</p>
                <button 
                  onClick={loadRebates}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    background: '#D4AF37',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : rebates.length === 0 ? (
              <div className="empty-state">
                <DollarSign size={48} />
                <p>No rebates added yet. Click "Add Rebate" to get started.</p>
              </div>
            ) : (
              rebates.map(rebate => (
                <div key={rebate.id} className="item-card">
                  <div className="item-info">
                    <h4>{rebate.title}</h4>
                    <p>
                      {rebate.brand} • {rebate.amountText || rebate.amounttext || `$${rebate.amount}`} • {rebate.status} {rebate.featured && '⭐ Featured'}
                    </p>
                  </div>
                  <div className="item-actions">
                    <button className="edit-btn" onClick={() => handleEditRebate(rebate)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="delete-btn" onClick={() => handleDeleteRebate(rebate.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <button 
            className="refresh-btn" 
            onClick={loadRebates} 
            disabled={rebatesLoading}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <RefreshCw size={16} className={rebatesLoading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      )}

      {/* Dealers Tab */}
      {activeTab === 'dealers' && (
        <div className="admin-section">
          <div className="section-header">
            <h2><Users size={24} /> Dealer Network</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="refresh-btn" 
                onClick={loadDealers}
                disabled={dealersLoading}
                style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <RefreshCw size={16} className={dealersLoading ? 'spin' : ''} />
                Refresh
              </button>
              <button className="add-button" onClick={() => { setShowDealerForm(true); setEditingDealer(null); setDealerForm(emptyDealer); setGeocodeStatus(null); }}>
                <Plus size={18} />
                Add Dealer
              </button>
            </div>
          </div>

          {showDealerForm && (
            <div className="admin-form">
              <h3>{editingDealer ? 'Edit Dealer' : 'Add New Dealer'}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Dealership Name *</label>
                  <input
                    type="text"
                    value={dealerForm.dealerName}
                    onChange={(e) => setDealerForm({ ...dealerForm, dealerName: e.target.value })}
                    placeholder="e.g., BoardCo"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    value={dealerForm.contactName}
                    onChange={(e) => setDealerForm({ ...dealerForm, contactName: e.target.value })}
                    placeholder="Primary contact"
                  />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    value={dealerForm.phone}
                    onChange={(e) => setDealerForm({ ...dealerForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={dealerForm.email}
                    onChange={(e) => setDealerForm({ ...dealerForm, email: e.target.value })}
                    placeholder="sales@dealer.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="text"
                    value={dealerForm.website}
                    onChange={(e) => setDealerForm({ ...dealerForm, website: e.target.value })}
                    placeholder="https://www.dealer.com"
                  />
                </div>
                <div className="form-group">
                  <label>Business Hours</label>
                  <input
                    type="text"
                    value={dealerForm.hours}
                    onChange={(e) => setDealerForm({ ...dealerForm, hours: e.target.value })}
                    placeholder="Mon-Fri: 9AM-6PM"
                  />
                </div>
              </div>

              {/* Address Section */}
              <div style={{ 
                background: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginTop: '1rem',
                marginBottom: '1rem'
              }}>
                <h4 style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MapPin size={16} /> Address & Location
                </h4>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Street Address</label>
                    <input
                      type="text"
                      value={dealerForm.address}
                      onChange={(e) => setDealerForm({ ...dealerForm, address: e.target.value })}
                      placeholder="123 Main St"
                    />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={dealerForm.city}
                      onChange={(e) => setDealerForm({ ...dealerForm, city: e.target.value })}
                      placeholder="City"
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={dealerForm.state}
                      onChange={(e) => setDealerForm({ ...dealerForm, state: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="CA"
                      maxLength={2}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code</label>
                    <input
                      type="text"
                      value={dealerForm.zipCode}
                      onChange={(e) => setDealerForm({ ...dealerForm, zipCode: e.target.value })}
                      placeholder="12345"
                      maxLength={5}
                    />
                  </div>
                </div>

                {/* Geocoding Section */}
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  alignItems: 'flex-end',
                  marginTop: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                    <label>Latitude</label>
                    <input
                      type="number"
                      step="0.0001"
                      value={dealerForm.latitude}
                      onChange={(e) => setDealerForm({ ...dealerForm, latitude: e.target.value })}
                      placeholder="e.g., 40.1833"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                    <label>Longitude</label>
                    <input
                      type="number"
                      step="0.0001"
                      value={dealerForm.longitude}
                      onChange={(e) => setDealerForm({ ...dealerForm, longitude: e.target.value })}
                      placeholder="e.g., -111.6361"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleGeocodeAddress}
                    disabled={geocoding}
                    style={{
                      padding: '0.75rem 1rem',
                      background: geocoding ? '#ccc' : '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: geocoding ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      whiteSpace: 'nowrap',
                      marginBottom: '0.5rem'
                    }}
                  >
                    {geocoding ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Finding...
                      </>
                    ) : (
                      <>
                        <MapPin size={16} />
                        Auto-Locate
                      </>
                    )}
                  </button>
                </div>
                {geocodeStatus && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    background: geocodeStatus.type === 'success' ? '#E8F5E9' : '#FFEBEE',
                    color: geocodeStatus.type === 'success' ? '#2E7D32' : '#C62828',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {geocodeStatus.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {geocodeStatus.message}
                  </div>
                )}
              </div>

              {/* Additional Info */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Dealer Type</label>
                  <select
                    value={dealerForm.dealerType}
                    onChange={(e) => setDealerForm({ ...dealerForm, dealerType: e.target.value })}
                  >
                    <option value="Authorized">Authorized</option>
                    <option value="Premier">Premier</option>
                    <option value="Service Center">Service Center</option>
                    <option value="Parts Only">Parts Only</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={dealerForm.status}
                    onChange={(e) => setDealerForm({ ...dealerForm, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Brands</label>
                  <input
                    type="text"
                    value={dealerForm.brands}
                    onChange={(e) => setDealerForm({ ...dealerForm, brands: e.target.value })}
                    placeholder="Centurion, Supreme"
                  />
                </div>
                <div className="form-group">
                  <label>Services</label>
                  <input
                    type="text"
                    value={dealerForm.services}
                    onChange={(e) => setDealerForm({ ...dealerForm, services: e.target.value })}
                    placeholder="Sales, Service, Parts"
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Notes / Special Offers</label>
                <textarea
                  value={dealerForm.notes}
                  onChange={(e) => setDealerForm({ ...dealerForm, notes: e.target.value })}
                  placeholder="e.g., Exclusive 2025 Ri Series in stock!"
                  rows={2}
                />
              </div>
              <div className="form-actions">
                <button className="save-btn" onClick={handleSaveDealer}>
                  <Save size={16} /> Save Dealer
                </button>
                <button className="cancel-btn" onClick={() => { setShowDealerForm(false); setEditingDealer(null); setGeocodeStatus(null); }}>
                  <X size={16} /> Cancel
                </button>
              </div>
            </div>
          )}

          <div className="items-list">
            {dealersLoading ? (
              <div className="empty-state">
                <Loader2 size={48} className="spin" />
                <p>Loading dealers...</p>
              </div>
            ) : dealersError ? (
              <div className="empty-state" style={{ color: '#8B0000' }}>
                <AlertCircle size={48} />
                <p>{dealersError}</p>
                <button 
                  onClick={loadDealers}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    background: '#D4AF37',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : dealers.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>No dealers found. Click "Add Dealer" to get started.</p>
              </div>
            ) : (
              dealers.map(dealer => (
                <div key={dealer.id} className="item-card">
                  <div className="item-info">
                    <h4>
                      {dealer.dealerName} 
                      {dealer.dealerType === 'Premier' && <span style={{ marginLeft: '0.5rem' }}>⭐</span>}
                      {dealer.status !== 'Active' && (
                        <span style={{ 
                          marginLeft: '0.5rem', 
                          fontSize: '0.7rem', 
                          padding: '0.2rem 0.5rem', 
                          background: dealer.status === 'Inactive' ? '#ffebee' : '#fff3e0',
                          color: dealer.status === 'Inactive' ? '#c62828' : '#e65100',
                          borderRadius: '4px'
                        }}>
                          {dealer.status}
                        </span>
                      )}
                    </h4>
                    <p style={{ margin: '0.25rem 0', color: '#666' }}>
                      {dealer.address && `${dealer.address}, `}{dealer.city}, {dealer.state} {dealer.zipCode}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>
                      {dealer.phone} • {dealer.email}
                      {dealer.latitude && dealer.longitude && (
                        <span style={{ marginLeft: '0.5rem', color: '#4CAF50' }}>
                          <MapPin size={12} style={{ verticalAlign: 'middle' }} /> Located
                        </span>
                      )}
                    </p>
                    {dealer.notes && (
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', fontStyle: 'italic', color: '#999' }}>
                        "{dealer.notes}"
                      </p>
                    )}
                  </div>
                  <div className="item-actions">
                    <button className="edit-btn" onClick={() => handleEditDealer(dealer)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="delete-btn" onClick={() => handleDeleteDealer(dealer.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="admin-help">
        <AlertCircle size={20} />
        <div>
          <h4>Admin Tips</h4>
          <ul>
            <li><strong>Issues:</strong> Track and manage customer complaints and issues through their lifecycle stages</li>
            <li><strong>Factory Boats:</strong> Manage boats that appear on the Factory Direct page</li>
            <li><strong>Rebates:</strong> Configure rebates and incentives shown on the Rebates Schedule</li>
            <li><strong>Dealers:</strong> Manage dealer network information for the Contact page</li>
            <li>Issue statuses: Open → In Progress → Under Review → Resolved → Closed</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

